<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl mb-1">Cancellations</h1>
  </div>

  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <CancellationsControls
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="cancellationsStore.loading"
      :columns="cancellationsTableColumns"
      :data="paginatedCancellations || []"
      :total-count="totalCount"
      :page-number="pageNumber"
      :page-size="pageSize"
      @update:page="onUpdatePage"
      @update:page-size="onUpdatePageSize"
    />
  </div>
</template>

<script setup lang="ts">
import type { OrderServiceGetManyRequest } from '@/models/orders';
import { useCancellationStore } from '@/stores/cancellations';
import { addMetric } from '@/utils/monitoring.utils';
import type { DateRange } from 'radix-vue';
import { computed, onMounted, ref, watch } from 'vue';
import DataTable from '../../DataTable.vue';
import CancellationsControls from '../cancellations-controls/CancellationsControls.vue';
import { cancellationsTableColumns } from './CancellationsTableColumns';

const cancellationsStore = useCancellationStore();

const cancellationsParams = ref<OrderServiceGetManyRequest>({
  populate: ['plans', 'plans.payments', 'plans.payments.connectorData', 'plans.benefactor', 'supplier'],
});

const previousParams = ref('');

const totalCount = ref(1);
const pageNumber = ref(1);
const pageSize = ref(10);

const paginatedCancellations = computed(() => {
  return cancellationsStore.unresolvedCancellations?.slice(
    (pageNumber.value - 1) * pageSize.value,
    pageNumber.value * pageSize.value,
  );
});

onMounted(async () => {
  await onUpdateFilter(cancellationsParams.value, true);
});

const onUpdateFilter = async (params: OrderServiceGetManyRequest, skipCheck = false) => {
  const currentParams = JSON.stringify(params);

  if (!skipCheck && currentParams === previousParams.value) {
    return;
  }

  previousParams.value = currentParams;

  await cancellationsStore.fetchUnresolvedCancellations(params);
};

const onDateRangeChange = (value: DateRange): void => {
  const updatedParams: OrderServiceGetManyRequest = {
    ...cancellationsParams.value,
    from: value.start ? `${new Date(`${value.start.year}-${value.start.month}-${value.start.day}`)}` : undefined,
    to: value.end ? `${new Date(`${value.end.year}-${value.end.month}-${value.end.day}`)}` : undefined,
  };

  cancellationsParams.value = updatedParams;

  onUpdateFilter(updatedParams);
};

const onClearFilters = (): void => {
  const updatedParams: OrderServiceGetManyRequest = {
    limit: 500,
  };

  cancellationsParams.value = updatedParams;

  onUpdateFilter(updatedParams);
};

const onSearchTermChange = (value: string): void => {
  pageNumber.value = 1;

  const updatedParams: OrderServiceGetManyRequest = {
    ...cancellationsParams.value,
    search: value.toUpperCase(),
  };

  cancellationsParams.value = updatedParams;

  onUpdateFilter(updatedParams);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
};

const onUpdatePageSize = (size: number) => {
  pageSize.value = size;
};

watch(
  () => cancellationsStore.unresolvedCancellations,
  (unresolvedCancellations) => {
    if (!unresolvedCancellations) return;

    totalCount.value = unresolvedCancellations.length;
    pageNumber.value = 1;

    addMetric('cancellations-table', {
      status: 'success',
      channel: 'merchant',
      method: 'fetchOrders',
    });
  },
);
</script>
